import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { absMediaUrl } from "../utils/mediaUrl";
import { DjUser } from "../components/admin-dj/types";
import "./AdminReviewPage.css";

// Where the Approve / Decline buttons in the founder's "New talent to review"
// email land (server lib/talentReviewEmail.js). One confirm tap, then done —
// the email can't approve by itself because mail apps open every link to scan it.
// Same calls as the Approvals tab (components/AdminApprovalsTab.tsx).

type Action = "approve" | "decline";
type Loaded = DjUser & { isActivated?: boolean; links?: string[]; bankCountry?: string };

// Declined = declinedAt (server lib/approval.js). Before 1.5.1 a decline was a
// freeze with this reason; the server's one-time backfill moves those over.
const DECLINED_REASON = "application_declined";

export default function AdminReviewPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const action: Action = params.get("do") === "decline" ? "decline" : "approve";

  const [dj, setDj] = useState<Loaded | null>(null);
  const [missing, setMissing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<Action | null>(null);

  useEffect(() => {
    setLoadFailed(false);
    adminApi
      .get(`/dj/${id}`)
      .then((r) => (r.data?.user ? setDj(r.data.user) : setMissing(true)))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 400) setMissing(true);
        // 401: apiAdmin is already sending us to sign-in (and back here after).
        else if (status !== 401) setLoadFailed(true);
      });
  }, [id, attempt]);

  const back = <Link className="arv-back" to="/admin/dashboard?tab=approvals">Back to Approvals</Link>;

  if (missing) {
    return (
      <main className="arv">
        <p className="arv-q">We couldn't find this talent</p>
        <p className="arv-meta">They may have deleted their account.</p>
        {back}
      </main>
    );
  }
  if (loadFailed) {
    return (
      <main className="arv">
        <p className="arv-q">That didn't load</p>
        <p className="arv-meta">Check your connection and try again.</p>
        <button className="arv-btn arv-yes" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        {back}
      </main>
    );
  }
  if (!dj) return <main className="arv"><p className="arv-meta">Loading…</p></main>;

  const name = dj.name || "This talent";
  const ig = (dj.instagram || "").trim().replace(/^@+/, "");
  const link = (dj.links || []).find((l) => /^https?:\/\//i.test(l));
  const avatarSrc = absMediaUrl(dj.avatarUrl);
  const declined = !dj.isActivated && (!!dj.declinedAt || (!!dj.isFrozen && dj.freezeReason === DECLINED_REASON));
  // Changed your mind? The email's Approve button still approves a declined
  // talent. (Until 1.5.1 the way back was Unfreeze; a decline is no longer a
  // freeze, so it is this page.)
  const settled: Action | null = done || (dj.isActivated ? "approve" : declined && action === "decline" ? "decline" : null);

  const decide = async () => {
    setBusy(true);
    setError("");
    try {
      await adminApi.patch(`/users/${dj._id}/${action}`);
      setDone(action);
    } catch (err: any) {
      setError(err.response?.data?.msg || "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="arv">
      {avatarSrc ? (
        <img className="arv-avatar" src={avatarSrc} alt="" />
      ) : (
        <div className="arv-avatar">{name[0].toUpperCase()}</div>
      )}

      {settled ? (
        <>
          <p className="arv-q">{name}</p>
          <p className={settled === "approve" ? "arv-done" : "arv-done arv-done-muted"}>
            {settled === "approve"
              ? done ? "✓ Approved. Their QR code is live." : "✓ Already approved."
              : done ? "Declined. They got the email." : "Already declined."}
          </p>
          {back}
        </>
      ) : (
        <>
          <p className="arv-q">{action === "approve" ? `Approve ${name}?` : `Decline ${name}?`}</p>
          {ig ? (
            <a className="arv-ig" href={`https://instagram.com/${encodeURIComponent(ig)}`} target="_blank" rel="noopener noreferrer">
              @{ig} on Instagram ›
            </a>
          ) : link ? (
            <a className="arv-ig" href={link} target="_blank" rel="noopener noreferrer">
              {link.replace(/^https?:\/\//i, "")} ›
            </a>
          ) : null}
          <p className="arv-meta">
            {action === "approve"
              ? `${declined ? "You declined them earlier. " : ""}They get the “You're ready to earn” email right away.`
              : "They keep their free page; paid requests stay off. They get the “couldn't verify” email."}
          </p>
          {error && <p className="arv-error">{error}</p>}
          <button className={action === "approve" ? "arv-btn arv-yes" : "arv-btn arv-no-primary"} disabled={busy} onClick={decide}>
            {busy ? "One sec…" : action === "approve" ? "Approve" : "Decline"}
          </button>
          <Link className="arv-btn arv-no" to="/admin/dashboard?tab=approvals">Not now</Link>
        </>
      )}
    </main>
  );
}
